import { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout, message, theme } from "antd";
import Chatbot from "../AskDatatram/ChatLayout";
import ErrorFallback from "src/components/reusable/ErrorFallback/ErrorFallback";
import ApiErrorContext from "src/contexts/ApiErrorContext";
import { useNotification } from "src/contexts/NotificationContext";
import { DashboardApiError, fetchAllDashboardsAPI } from "src/services/AccountDashboardAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import AnnouncementPopupWrapper from "./Announcement/AnnouncementPopupWrapper";
import { DASHBOARD_APP_BASE, resolveMatchedDashboardRoute } from "./Config/DashboardRoutes";
import HomepageHeader from "./Header/HomepageHeader";
import { useHomepageSession } from "./Hooks/UseHomepageSession";
import Sidebar from "./Sidebar/Sidebar";

const { Content } = Layout;

const AI_CHAT_ROUTE = "/account/ai-chat";

const HomepageLayout = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const apiErrorContext = useContext(ApiErrorContext);
  const hasGlobalApiError = apiErrorContext?.hasApiError ?? false;
  const { refreshCount } = useNotification();
  const hasFetchedUnread = useRef(false);
  const hasFetchedDashboards = useRef(false);
  const [hasApiError, setHasApiError] = useState(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useHomepageSession(new URLSearchParams(location.search).get("token"));

  const isAiChatRoute = location.pathname === AI_CHAT_ROUTE;
  const isDashboardRoute =
    location.pathname === DASHBOARD_APP_BASE ||
    location.pathname === `${DASHBOARD_APP_BASE}/` ||
    Boolean(resolveMatchedDashboardRoute(location.pathname));

  useEffect(() => {
    if (hasFetchedUnread.current) {
      return;
    }
    hasFetchedUnread.current = true;
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    document.body.style.overflowX = "hidden";
  });

  const fetchDashboards = async () => {
    try {
      const userId = localStorage.getItem("user_id");
      const apiKey = localStorage.getItem("api_key");

      if (!userId || !apiKey) {
        message.error("Missing credentials. Please login again.");
        return;
      }

      await fetchAllDashboardsAPI(userId, apiKey, getApiLangFromI18n(i18n));
      setHasApiError(false);
    } catch (err) {
      message.error(
        err instanceof DashboardApiError
          ? err.message
          : "Failed to fetch dashboards. Please try again."
      );
      if (isDashboardRoute) {
        setHasApiError(true);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedDashboards.current) {
      return;
    }
    hasFetchedDashboards.current = true;

    fetchDashboards();
  }, []);

  const handleApiRetry = () => {
    setHasApiError(false);
    fetchDashboards();
  };

  const handleGlobalRetry = () => {
    apiErrorContext?.resetApiError();
    window.location.reload();
  };

  const renderContent = () => {
    if (hasGlobalApiError) {
      return <ErrorFallback onRetry={handleGlobalRetry} />;
    }
    if (hasApiError && isDashboardRoute) {
      return <ErrorFallback onRetry={handleApiRetry} />;
    }
    return <Outlet />;
  };

  return (
    <Layout
      className="home-layout-shell"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "row" }}
    >
      <Sidebar />

      <Layout
        className={`home-layout-main responsive-margin ${isAiChatRoute ? "ai-chat-layout-main" : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100vh",
        }}
      >
        <HomepageHeader />

        <div
          style={{
            padding: "4px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            overflow: "hidden",
            background: isAiChatRoute ? "#f3f4f6" : undefined,
          }}
        >
          <Content
            className={isAiChatRoute ? "ai-chat-layout-content" : undefined}
            style={{
              background: colorBgContainer,
              padding: isAiChatRoute ? 0 : 10,
              flex: 1,
              minHeight: 0,
              overflow: isAiChatRoute ? "hidden" : "auto",
            }}
          >
            {renderContent()}
          </Content>
        </div>
        {!isAiChatRoute && <Chatbot />}
      </Layout>
      <AnnouncementPopupWrapper />
    </Layout>
  );
};

export default HomepageLayout;
